import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@bpinternal/ui-kit-next'
import { X, Zap, ZapOff } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { showUpsellDialog, useQuery } from '../../../services'
import { showConfirmationPrompt, showDialog } from '@bpinternal/ui-kit'
import { useDeleteBot, useUpdateAlwaysAlive, useUsage } from '../../../hooks'
import { useRouter } from '@tanstack/react-router'
import { CopyToBotDialog } from './CopyToBotDialog'
import { BotIcon } from '../../../componentsV2'

type Props = {
  className?: string
  children: ReactNode
  setAlwaysAlivePending?: (value: boolean) => void
  useCopyToBot?: boolean
  workspaceId: string
  botId: string
  userId: string
  botName?: string
}

export const BotCardDropdown = ({ className, children, botId, workspaceId, botName, ...props }: Props) => {
  const router = useRouter()

  const { data: bots = [] } = useQuery('workspaces_/$workspaceId_/bots_', { workspaceId })
  const { mutate: deleteBot } = useDeleteBot(workspaceId)
  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId })

  const { always_alive: alwaysAliveUsage } = useUsage({ workspaceId, quotas: ['always_alive'] })

  const isAlwaysAlive = bot?.alwaysAlive
  const alwaysAliveDisabled = alwaysAliveUsage.value >= alwaysAliveUsage.quota && isAlwaysAlive

  const { mutate: updateAlwaysAlive, isPending: alwaysAlivePending } = useUpdateAlwaysAlive()

  const [open, setOpen] = useState(false) // Need to controle the state for now because of a bug in radix, see: https://github.com/radix-ui/primitives/issues/837#issuecomment-2716644471

  useEffect(() => {
    if (props.setAlwaysAlivePending) {
      props.setAlwaysAlivePending(alwaysAlivePending)
    }
  }, [alwaysAlivePending])

  const copyToBot = async (targetBotId: string, targetBotName: string) => {
    if (!bot) {
      return
    }

    setOpen(false)
    showDialog(
      ({ close }) => (
        <CopyToBotDialog
          close={close}
          workspaceId={workspaceId}
          sourceBotId={bot.id}
          targetBotId={targetBotId}
          sourceBotName={bot.name}
          targetBotName={targetBotName}
        />
      ),
      {
        title: `Copying to ${targetBotName}`,
      }
    )
  }
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className={className}>
        <DropdownMenuItem
          onClick={() => {
            if (!alwaysAliveDisabled) {
              setOpen(false)
              showConfirmationPrompt(
                <span>
                  Are you sure you want to {isAlwaysAlive ? 'disable' : 'enable'} the always alive mode for the bot{' '}
                  <span className="font-bold">{botName}</span>?
                </span>,
                {
                  title: isAlwaysAlive ? 'Disable Always Alive' : 'Enable Always Alive',
                }
              ).then(() =>
                updateAlwaysAlive({
                  botId,
                  workspaceId,
                  alwaysAlive: !isAlwaysAlive,
                })
              )
            } else {
              setOpen(false)
              showUpsellDialog({
                quota: 'always_alive',
                workspaceId,
              }).then(
                () =>
                  void router.navigate({
                    to: '/workspaces/$workspaceId/billing',
                    params: { workspaceId },
                  })
              )
            }
          }}
        >
          <span>{isAlwaysAlive ? 'Disable Always Alive' : 'Enable Always Alive'}</span>
          {isAlwaysAlive ? <ZapOff className="ml-auto pl-0.5" /> : <Zap className="ml-auto pl-0.5" />}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="data-[disabled]:text-gray-10" disabled={bots.length <= 1}>
            Copy to bot
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {bots
              .filter((bot) => bot.id !== botId)
              .map((bot) => (
                <DropdownMenuItem key={bot.id} onClick={() => copyToBot(bot.id, bot.name)}>
                  <BotIcon id={bot.id} size={'2'} />
                  <span>{bot.name}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-danger-600 focus:bg-danger-100 focus:text-danger-700"
            onClick={() => {
              setOpen(false)
              showConfirmationPrompt(`Are you sure you want to delete the bot "${name ?? bot?.name}"?`, {
                title: 'Delete bot',
                variant: 'danger',
              }).then(() =>
                deleteBot({
                  id: botId,
                  options: {},
                })
              )
            }}
          >
            <span>Delete</span>
            <X className="ml-auto pl-0.5" />
          </DropdownMenuItem>
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
